import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { GoogleButton } from "@/components/GoogleButton";
import {
    ShieldCheck, Users, Trophy, Gavel, Package, BarChart3, Lock, Headphones,
    Eye, EyeOff, Loader2, ArrowRight, Mail, Sparkles, BadgeCheck,
} from "lucide-react";

const img = (id, w = 600) => `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

const HERO_FEATURES = [
    { icon: ShieldCheck, title: "Fair. Transparent. Secure.", text: "Every auction. Every time." },
    { icon: Users, title: "Join thousands of smart buyers", text: "and trusted sellers." },
    { icon: Trophy, title: "Real value. Real results.", text: "Only on OmniAuc." },
];

const TRUST = [
    { icon: ShieldCheck, title: "Secure & Verified", text: "Advanced protection for your account" },
    { icon: Lock, title: "Private & Confidential", text: "Your data is always safe with us" },
    { icon: BadgeCheck, title: "Trusted Community", text: "Verified users. Fair auctions. Always." },
    { icon: Headphones, title: "Here to Help", text: "Support when you need it most" },
];

const PERKS = [
    { icon: Gavel, title: "Bid with Confidence", text: "Submit your best bid and let the market decide." },
    { icon: Package, title: "Sell with Ease", text: "List your items quickly and reach serious buyers." },
    { icon: BarChart3, title: "Track Everything", text: "Monitor your bids, wins, and sales in one place." },
    { icon: ShieldCheck, title: "Built on Fairness", text: "No sniping. No tricks. Just a level playing field." },
];

const CTA_IMAGES = [
    { id: "1523275335684-37898b6baf30", alt: "Luxury watch" },
    { id: "1516035069371-29a1b244cc32", alt: "Mirrorless camera" },
    { id: "1496181133206-80ce9b88a853", alt: "Laptop" },
];

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState(() => localStorage.getItem("omniauc_remember_email") || "");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [remember, setRemember] = useState(() => !!localStorage.getItem("omniauc_remember_email"));
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            if (remember) localStorage.setItem("omniauc_remember_email", email);
            else localStorage.removeItem("omniauc_remember_email");
            toast.success("Welcome back");
            navigate("/auctions");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="login-page">
            {/* ---------------- HERO ---------------- */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_55%_at_50%_0%,rgba(214,158,46,0.12)_0%,rgba(214,158,46,0)_55%)]" />
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(50%_50%_at_85%_30%,rgba(33,140,255,0.12)_0%,rgba(33,140,255,0)_60%)]" />
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20">
                    {/* Left: welcome copy */}
                    <div className="order-2 lg:order-1">
                        <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
                            Welcome Back!
                            <span className="mt-1 block bg-gradient-to-r from-brass to-[hsl(38_92%_62%)] bg-clip-text text-transparent">
                                Let the Fair Market Decide.
                            </span>
                        </h1>
                        <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                            Sign in to your OmniAuc account and get back to smarter bidding and selling.
                        </p>
                        <ul className="mt-8 space-y-5">
                            {HERO_FEATURES.map(({ icon: Icon, title, text }) => (
                                <li key={title} className="flex items-start gap-4">
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brass/10 text-brass ring-1 ring-brass/20">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <p className="text-sm leading-relaxed">
                                        <span className="font-semibold text-foreground">{title}</span>{" "}
                                        <span className="text-muted-foreground">{text}</span>
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right: login card */}
                    <div className="order-1 mx-auto w-full max-w-md lg:order-2">
                        <Card className="relative overflow-hidden p-6 shadow-2xl sm:p-8">
                            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brass/50 to-transparent" />
                            <div className="text-center">
                                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brass/10 text-brass ring-1 ring-brass/20">
                                    <Sparkles className="h-6 w-6" />
                                </span>
                                <h2 className="mt-4 font-display text-2xl font-bold tracking-tight">Welcome Back</h2>
                                <p className="mt-1 text-sm text-muted-foreground">Sign in to your account</p>
                            </div>

                            <form onSubmit={submit} className="mt-7 space-y-4">
                                <div className="relative">
                                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        data-testid="login-email" type="email" autoComplete="email"
                                        value={email} onChange={(e) => setEmail(e.target.value)} required
                                        placeholder="Email address" className="pl-10" aria-label="Email address"
                                    />
                                </div>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        data-testid="login-password" type={showPw ? "text" : "password"} autoComplete="current-password"
                                        value={password} onChange={(e) => setPassword(e.target.value)} required
                                        placeholder="Password" className="px-10" aria-label="Password"
                                    />
                                    <button
                                        type="button" onClick={() => setShowPw((v) => !v)} data-testid="login-toggle-password"
                                        aria-label={showPw ? "Hide password" : "Show password"}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                                    >
                                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>

                                <div className="flex items-center justify-between">
                                    <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground" htmlFor="remember-me">
                                        <Checkbox id="remember-me" checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
                                        Remember me
                                    </label>
                                    <Link to="/account/forgot-password" data-testid="login-forgot-link" className="text-sm font-medium text-brass hover:underline">
                                        Forgot Password?
                                    </Link>
                                </div>

                                <Button type="submit" size="lg" className="w-full rounded-[10px] text-base font-semibold" disabled={loading} data-testid="login-submit">
                                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log in
                                </Button>
                            </form>

                            {/* Google appears automatically when OAuth is configured */}
                            <GoogleButton />

                            <p className="mt-6 text-center text-sm text-muted-foreground">
                                Don't have an account?{" "}
                                <Link to="/signup" data-testid="login-signup-link" className="font-semibold text-brass hover:underline">Sign Up</Link>
                            </p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* ---------------- TRUST STRIP ---------------- */}
            <section className="border-b border-border bg-[hsl(221_59%_7%)]">
                <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 sm:grid-cols-2 lg:grid-cols-4">
                    {TRUST.map(({ icon: Icon, title, text }) => (
                        <div key={title} className="flex items-start gap-4" data-testid="login-trust-item">
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-border bg-card text-electric">
                                <Icon className="h-5 w-5" />
                            </span>
                            <div>
                                <h3 className="font-display text-sm font-bold text-brass">{title}</h3>
                                <p className="mt-0.5 text-sm text-muted-foreground">{text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ---------------- ONE ACCOUNT. ENDLESS OPPORTUNITIES ---------------- */}
            <section className="bg-background">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                            One Account. <span className="text-brass">Endless Opportunities.</span>
                        </h2>
                        <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-brass" />
                        <p className="mt-5 text-base text-muted-foreground">
                            Access auctions, track your activity, manage your listings, and connect with a community built on fairness.
                        </p>
                    </div>

                    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {PERKS.map(({ icon: Icon, title, text }) => (
                            <div key={title} className="rounded-2xl border border-border bg-card p-6 text-center transition-colors hover:border-brass/40" data-testid="login-perk-card">
                                <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brass/20 to-electric/10 ring-1 ring-brass/30">
                                    <Icon className="h-7 w-7 text-brass" />
                                    <div className="absolute inset-0 -z-10 rounded-2xl bg-brass/25 blur-xl" />
                                </div>
                                <h3 className="mt-5 font-display text-base font-bold">{title}</h3>
                                <p className="mx-auto mt-2 max-w-[14rem] text-sm leading-relaxed text-muted-foreground">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- READY TO JUMP BACK IN ---------------- */}
            <section className="relative overflow-hidden border-t border-border bg-[hsl(221_59%_7%)]">
                <div className="absolute inset-y-0 right-0 -z-10 w-2/3 bg-[radial-gradient(60%_120%_at_100%_50%,rgba(214,158,46,0.16)_0%,rgba(214,158,46,0)_60%)]" />
                <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4 py-12 sm:px-6 lg:flex-row lg:justify-between">
                    <div className="text-center lg:text-left">
                        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Ready to jump back in?</h2>
                        <p className="mt-2 text-muted-foreground">The fair auction marketplace is waiting for you.</p>
                        <Button asChild size="lg" className="mt-6 rounded-[10px]" data-testid="login-browse-cta">
                            <Link to="/auctions">Browse auctions <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                    </div>
                    <div className="flex items-center gap-4">
                        {CTA_IMAGES.map(({ id, alt }) => (
                            <div key={id} className="h-24 w-24 overflow-hidden rounded-xl border border-border sm:h-28 sm:w-28">
                                <img src={img(id, 300)} alt={alt} loading="lazy" className="h-full w-full object-cover" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
